import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { PageHeader } from "@/components/PageHeader";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Progress } from "@/components/ui/progress";
import { 
  RefreshCw, 
  TrendingUp, 
  TrendingDown,
  Users,
  Building2,
  PoundSterling,
  Target,
  Clock,
  CheckCircle,
  XCircle,
  AlertTriangle,
  BarChart3,
  PieChart,
  Loader2,
  ArrowUpRight,
  ArrowDownRight
} from "lucide-react";
import { toast } from "sonner";

const SF_BLUE = "#3B70E0";

// API base
const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function FundingStats() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [stats, setStats] = useState(null);
  const [period, setPeriod] = useState("month");

  useEffect(() => {
    loadStats();
  }, [period]);

  const loadStats = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('sf_token');
      const response = await fetch(`${API_URL}/api/stats/funding-analytics?period=${period}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setStats(data);
    } catch (error) {
      console.error("Failed to load stats:", error);
      toast.error("Failed to load analytics");
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadStats();
    setRefreshing(false);
    toast.success("Stats refreshed");
  };

  const formatCurrency = (value) => {
    if (!value) return "£0";
    return `£${value.toLocaleString()}`;
  };

  const formatPercent = (value) => {
    if (!value) return "0%";
    return `${value.toFixed(1)}%`;
  };

  if (loading && !stats) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div data-testid="funding-stats-page">
      <PageHeader
        title="Funding Analytics"
        description="Pipeline performance, broker analysis & deal flow insights"
      >
        <div className="flex items-center gap-3">
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="w-[140px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="week">This Week</SelectItem>
              <SelectItem value="month">This Month</SelectItem>
              <SelectItem value="quarter">This Quarter</SelectItem>
              <SelectItem value="year">This Year</SelectItem>
              <SelectItem value="all">All Time</SelectItem>
            </SelectContent>
          </Select>
          <Button 
            variant="outline" 
            onClick={handleRefresh}
            disabled={refreshing}
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </PageHeader>

      {/* Key Metrics Row */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Pipeline Value</p>
                <p className="text-2xl font-bold">{formatCurrency(stats?.pipeline?.total_value)}</p>
                <p className="text-xs text-muted-foreground mt-1">
                  {stats?.pipeline?.total_deals || 0} active deals
                </p>
              </div>
              <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                <PoundSterling className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Funded This Month</p>
                <p className="text-2xl font-bold">{formatCurrency(stats?.funded?.month_value)}</p>
                <p className="text-xs text-emerald-600 mt-1 flex items-center gap-1">
                  <CheckCircle className="w-3 h-3" />
                  {stats?.funded?.month_count || 0} deals
                </p>
              </div>
              <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-emerald-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Conversion Rate</p>
                <p className="text-2xl font-bold">{formatPercent(stats?.conversion?.rate)}</p>
                <p className="text-xs text-muted-foreground mt-1">
                  Offers → Funded
                </p>
              </div>
              <div className="w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center">
                <Target className="w-6 h-6 text-purple-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Avg Deal Size</p>
                <p className="text-2xl font-bold">{formatCurrency(stats?.averages?.deal_size)}</p>
                <p className="text-xs text-muted-foreground mt-1">
                  Avg {stats?.averages?.days_to_fund || 0} days to fund
                </p>
              </div>
              <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center">
                <BarChart3 className="w-6 h-6 text-amber-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="pipeline" className="space-y-4">
        <TabsList>
          <TabsTrigger value="pipeline">Pipeline Stages</TabsTrigger>
          <TabsTrigger value="brokers">Broker Performance</TabsTrigger>
          <TabsTrigger value="workflow">Workflow Analytics</TabsTrigger>
          <TabsTrigger value="team">Team Performance</TabsTrigger>
        </TabsList>

        {/* Pipeline Stages Tab */}
        <TabsContent value="pipeline" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Pipeline Funnel */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Deal Pipeline</CardTitle>
                <CardDescription>Current deals by stage</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                {stats?.pipeline?.stages?.map((stage, idx) => (
                  <div key={idx} className="space-y-1">
                    <div className="flex items-center justify-between text-sm">
                      <span className="truncate">{stage.name}</span>
                      <span className="font-medium">{stage.count} ({formatCurrency(stage.value)})</span>
                    </div>
                    <Progress 
                      value={(stage.count / (stats?.pipeline?.total_deals || 1)) * 100} 
                      className="h-2"
                    />
                  </div>
                ))}
              </CardContent>
            </Card>

            {/* Status Breakdown */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Deal Status Overview</CardTitle>
                <CardDescription>Quick snapshot of all deal statuses</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-3">
                  <div className="p-3 rounded-lg bg-emerald-50 border border-emerald-200">
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-emerald-600" />
                      <span className="text-sm font-medium text-emerald-800">Funded</span>
                    </div>
                    <p className="text-2xl font-bold text-emerald-700 mt-1">{stats?.status_breakdown?.funded || 0}</p>
                  </div>
                  <div className="p-3 rounded-lg bg-blue-50 border border-blue-200">
                    <div className="flex items-center gap-2">
                      <Clock className="w-4 h-4 text-blue-600" />
                      <span className="text-sm font-medium text-blue-800">In Progress</span>
                    </div>
                    <p className="text-2xl font-bold text-blue-700 mt-1">{stats?.status_breakdown?.in_progress || 0}</p>
                  </div>
                  <div className="p-3 rounded-lg bg-amber-50 border border-amber-200">
                    <div className="flex items-center gap-2">
                      <AlertTriangle className="w-4 h-4 text-amber-600" />
                      <span className="text-sm font-medium text-amber-800">Pending Action</span>
                    </div>
                    <p className="text-2xl font-bold text-amber-700 mt-1">{stats?.status_breakdown?.pending || 0}</p>
                  </div>
                  <div className="p-3 rounded-lg bg-red-50 border border-red-200">
                    <div className="flex items-center gap-2">
                      <XCircle className="w-4 h-4 text-red-600" />
                      <span className="text-sm font-medium text-red-800">Declined/Rejected</span>
                    </div>
                    <p className="text-2xl font-bold text-red-700 mt-1">{stats?.status_breakdown?.declined || 0}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Ready to Fund */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                Ready for Docs / Funding
              </CardTitle>
              <CardDescription>Deals ready for contract generation and funding</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="text-center p-4 rounded-lg bg-slate-50">
                  <p className="text-3xl font-bold" style={{ color: SF_BLUE }}>{stats?.ready_to_fund?.count || 0}</p>
                  <p className="text-sm text-muted-foreground">Deals Ready</p>
                </div>
                <div className="text-center p-4 rounded-lg bg-slate-50">
                  <p className="text-3xl font-bold" style={{ color: SF_BLUE }}>{formatCurrency(stats?.ready_to_fund?.value)}</p>
                  <p className="text-sm text-muted-foreground">Total Value</p>
                </div>
                <div className="text-center p-4 rounded-lg bg-slate-50">
                  <p className="text-3xl font-bold" style={{ color: SF_BLUE }}>{formatCurrency(stats?.ready_to_fund?.commission)}</p>
                  <p className="text-sm text-muted-foreground">Expected Commission</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Broker Performance Tab */}
        <TabsContent value="brokers" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Top Brokers by Volume */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Top Brokers by Deal Volume</CardTitle>
                <CardDescription>Number of deals submitted</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                {stats?.brokers?.by_volume?.slice(0, 10).map((broker, idx) => (
                  <div key={idx} className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center text-xs font-bold text-primary">
                        {idx + 1}
                      </div>
                      <span className="text-sm truncate max-w-[180px]">{broker.name}</span>
                    </div>
                    <div className="text-right">
                      <span className="font-medium">{broker.deals}</span>
                      <span className="text-xs text-muted-foreground ml-1">deals</span>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>

            {/* Top Brokers by Value */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Top Brokers by Funded Value</CardTitle>
                <CardDescription>Total value of funded deals</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                {stats?.brokers?.by_value?.slice(0, 10).map((broker, idx) => (
                  <div key={idx} className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-6 h-6 rounded-full bg-emerald-100 flex items-center justify-center text-xs font-bold text-emerald-700">
                        {idx + 1}
                      </div>
                      <span className="text-sm truncate max-w-[180px]">{broker.name}</span>
                    </div>
                    <div className="text-right">
                      <span className="font-medium">{formatCurrency(broker.value)}</span>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>

          {/* Broker Conversion Rates */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Broker Conversion Rates</CardTitle>
              <CardDescription>Success rate from submission to funding</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {stats?.brokers?.conversion?.slice(0, 6).map((broker, idx) => (
                  <div key={idx} className="p-3 rounded-lg border">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium truncate max-w-[150px]">{broker.name}</span>
                      <span className={`text-sm font-bold ${broker.rate >= 50 ? 'text-emerald-600' : broker.rate >= 30 ? 'text-amber-600' : 'text-red-600'}`}>
                        {formatPercent(broker.rate)}
                      </span>
                    </div>
                    <Progress value={broker.rate} className="h-2" />
                    <p className="text-xs text-muted-foreground mt-1">
                      {broker.funded} funded / {broker.total} submitted
                    </p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Workflow Analytics Tab */}
        <TabsContent value="workflow" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* SimplyFunded Portal Stats */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Portal Workflow Stats</CardTitle>
                <CardDescription>From your SimplyFunded portal</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div className="p-3 rounded-lg bg-blue-50">
                    <p className="text-xs text-muted-foreground">Contracts Generated</p>
                    <p className="text-xl font-bold">{stats?.workflow?.contracts_generated || 0}</p>
                  </div>
                  <div className="p-3 rounded-lg bg-purple-50">
                    <p className="text-xs text-muted-foreground">DocuSign Sent</p>
                    <p className="text-xl font-bold">{stats?.workflow?.docusign_sent || 0}</p>
                  </div>
                  <div className="p-3 rounded-lg bg-emerald-50">
                    <p className="text-xs text-muted-foreground">Bank Verified</p>
                    <p className="text-xl font-bold">{stats?.workflow?.bank_verified || 0}</p>
                  </div>
                  <div className="p-3 rounded-lg bg-amber-50">
                    <p className="text-xs text-muted-foreground">Pending Approval</p>
                    <p className="text-xl font-bold">{stats?.workflow?.pending_approval || 0}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Processing Time */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Average Processing Times</CardTitle>
                <CardDescription>Time spent in each workflow stage</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                {stats?.workflow?.stage_times?.map((stage, idx) => (
                  <div key={idx} className="flex items-center justify-between">
                    <span className="text-sm">{stage.name}</span>
                    <span className="text-sm font-medium">{stage.avg_hours}h avg</span>
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>

          {/* Bottlenecks */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-amber-500" />
                Potential Bottlenecks
              </CardTitle>
              <CardDescription>Deals that may need attention</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="p-4 rounded-lg border border-amber-200 bg-amber-50">
                  <p className="text-sm font-medium text-amber-800">Awaiting Bank Details</p>
                  <p className="text-2xl font-bold text-amber-700">{stats?.bottlenecks?.awaiting_bank || 0}</p>
                  <p className="text-xs text-amber-600">deals waiting</p>
                </div>
                <div className="p-4 rounded-lg border border-red-200 bg-red-50">
                  <p className="text-sm font-medium text-red-800">Stale DocuSign</p>
                  <p className="text-2xl font-bold text-red-700">{stats?.bottlenecks?.stale_docusign || 0}</p>
                  <p className="text-xs text-red-600">sent &gt;3 days ago</p>
                </div>
                <div className="p-4 rounded-lg border border-blue-200 bg-blue-50">
                  <p className="text-sm font-medium text-blue-800">LoanDisk Pending</p>
                  <p className="text-2xl font-bold text-blue-700">{stats?.bottlenecks?.loandisk_pending || 0}</p>
                  <p className="text-xs text-blue-600">not yet added</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Team Performance Tab */}
        <TabsContent value="team" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Team Assignments */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Deals by Team Member</CardTitle>
                <CardDescription>Current assignments</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                {stats?.team?.assignments?.map((member, idx) => (
                  <div key={idx} className="flex items-center justify-between p-2 rounded-lg hover:bg-slate-50">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                        <Users className="w-4 h-4 text-primary" />
                      </div>
                      <div>
                        <p className="text-sm font-medium">{member.name}</p>
                        <p className="text-xs text-muted-foreground">{member.role}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-medium">{member.active_deals}</p>
                      <p className="text-xs text-muted-foreground">active</p>
                    </div>
                  </div>
                ))}
                {(!stats?.team?.assignments || stats.team.assignments.length === 0) && (
                  <p className="text-sm text-muted-foreground text-center py-4">No assignments yet</p>
                )}
              </CardContent>
            </Card>

            {/* Funded by Team */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Funded Deals by Team Member</CardTitle>
                <CardDescription>This month's performance</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                {stats?.team?.funded?.map((member, idx) => (
                  <div key={idx} className="flex items-center justify-between">
                    <span className="text-sm">{member.name}</span>
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{member.count} deals</span>
                      <span className="text-sm text-emerald-600">{formatCurrency(member.value)}</span>
                    </div>
                  </div>
                ))}
                {(!stats?.team?.funded || stats.team.funded.length === 0) && (
                  <p className="text-sm text-muted-foreground text-center py-4">No funded deals this month</p>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
